import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { Box, CustomIcons, Stack, Text } from '@ltvco/refresh-lib/theme';
import {
  useSessionEscalation,
  useSession,
  useServiceToken,
} from '@ltvco/refresh-lib/v1';
import { useEffect } from 'react';

import { useQueryParams } from 'utils/useQueryParams';

import AuthenticatedContent from './AuthenticatedContent';
import { ErrorModal } from './ErrorModal/ErrorModal';
import { GettingStarted } from './GettingStarted/GettingStarted';
import { Loading } from './Loading';
import { sessionEscalationURL } from './Oxford/constants';
import { useOneTimePasscodeContext } from './Oxford/OneTimePasscodeContext';
import { useOxford } from './Oxford/Oxford';
export interface IIdMonitorContainerProps {
  isOnIdMonitorPage?: boolean;
}

export const IdMonitorContainer = ({
  isOnIdMonitorPage = false,
}: IIdMonitorContainerProps) => {
  const { phoneOnlyVariation } = useSubscriptionAccessConfigContext();

  const escalatedParam = useQueryParams().get('escalated');

  const { checkEmail, sessionAuthorized, checkSessionEscalation } =
    useSessionEscalation();
  const { refetch: getSessionEscalation } = checkSessionEscalation;

  const {
    getUserProfile,
    getAuthenticationQuestions,
    getAuthenticationCheckAnswers,
    setEnabledUserProfileQuery,
    isLoading: isOxfordLoading,
    isOxfordError,
    enabledUserProfileQuery,
  } = useOxford();

  const { data: userProfileData } = getUserProfile;
  const { data: authenticationQuestionsData } = getAuthenticationQuestions;
  const { data: authenticationCheckAnswersData } =
    getAuthenticationCheckAnswers;
  const authenticationResponse =
    authenticationQuestionsData ?? authenticationCheckAnswersData;

  const {
    session: { account },
  } = useSession();

  const { blackBoxId } = useOneTimePasscodeContext();

  const isEnrolled =
    account?.account.user_settings.credit_hub_enrollment_completed;

  const { refetch: refetchServiceToken, data: tokenData } = useServiceToken(
    'oxford',
    sessionEscalationURL
  );

  if (isOnIdMonitorPage) {
    document.title = 'ID Monitor - BeenVerified';
  }

  /**
   * Effect hook that handles session escalation based on enrollment status and escalation parameter.
   * Triggers session escalation check when either:
   * - User is enrolled in credit hub
   * - URL contains escalated=true parameter
   *
   * @dependencies {boolean} escalatedParam - URL parameter indicating if session should be escalated
   * @dependencies {boolean} isEnrolled - User's credit hub enrollment status
   * @dependencies {boolean} checkEmail - Current email check status
   * @dependencies {Function} getSessionEscalation - Function to trigger session escalation
   */
  useEffect(() => {
    if ((isEnrolled || escalatedParam === 'true') && !checkEmail) {
      getSessionEscalation();
    }
  }, [escalatedParam, isEnrolled, checkEmail, getSessionEscalation]);

  /**
   * Effect hook that manages service token fetching based on session authorization.
   * Automatically fetches a new service token when session is authorized but no token exists.
   *
   * @dependencies {boolean} sessionAuthorized - Current session authorization status
   * @dependencies {Function} refetchServiceToken - Function to fetch new service token
   * @dependencies {Object} tokenData - Current service token data
   */
  useEffect(() => {
    if (sessionAuthorized && !tokenData) {
      refetchServiceToken();
    }
  }, [sessionAuthorized, refetchServiceToken, tokenData]);

  /**
   * Effect hook that enables user profile query when all required conditions are met.
   * Enables the query when:
   * - Service token is available
   * - BlackBox ID is defined
   * - Session is authorized
   *
   * @dependencies {Object} tokenData - Service token data
   * @dependencies {Function} setEnabledUserProfileQuery - Function to enable/disable profile query
   * @dependencies {string|undefined} blackBoxId - Current BlackBox ID
   * @dependencies {boolean} sessionAuthorized - Current session authorization status
   */
  useEffect(() => {
    if (tokenData?.token && blackBoxId !== undefined && sessionAuthorized) {
      setEnabledUserProfileQuery(true);
    }
  }, [tokenData, setEnabledUserProfileQuery, blackBoxId, sessionAuthorized]);

  if (phoneOnlyVariation) {
    return null;
  }

  /**
   * Determines and renders the appropriate component based on the current authentication and user state.
   * This method handles multiple authentication scenarios including:
   * - Session authorization
   * - Oxford authentication
   * - One-time passcode verification
   * - User profile validation
   * - Knowledge-based authentication
   *
   * @returns {JSX.Element} The appropriate component to display based on the current state:
   * - AuthenticatedContent: When user is fully authenticated
   * - ErrorModal: For various error states (generic, authentication, profile issues)
   * - Loading: During loading states
   * - KnowledgeBasedAuthentication: When KBA questions are available
   * - SignUpForm: When user needs to sign up
   * - GettingStarted: Default view for unauthenticated users
   */
  const display = () => {
    if (sessionAuthorized && enabledUserProfileQuery) {
      if (isOxfordError) {
        return <ErrorModal type={'generic'} />;
      }

      if (isOxfordLoading || blackBoxId === undefined) {
        return <Loading />;
      }

      if (userProfileData !== undefined) {
        if (
          [
            userProfileData?.authenticated,
            authenticationResponse?.authenticated,
          ].includes(true)
        ) {
          return <AuthenticatedContent />;
        }
      }

      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={3}
          textAlign="center"
          sx={{
            minHeight: { xs: '50vh', md: '60vh' },
            maxWidth: 560,
            margin: '0 auto',
            padding: { xs: 3, md: 6 },
          }}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 96,
              height: 96,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
            })}
          >
            <CustomIcons.IdMonitorIcon
              color="primary"
              sx={{ fontSize: 56, display: 'block' }}
              viewBox="-4 -4 32 32"
            />
          </Box>
          <Text
            variant="h1"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', m: 0 }}
          >
            ID Monitor Deprecation
          </Text>
          <Text
            variant="h5"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontWeight: 400,
              maxWidth: 480,
              m: 0,
            })}
          >
            ID Monitor will be deprecated soon. New opt-ins are no longer
            supported.
          </Text>
        </Stack>
      );
    }
    return <GettingStarted />;
  };

  return (
    <Box id="id-monitor-container" className="id-monitor-container">
      {display()}
    </Box>
  );
};
